import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import LoadingSpinner from './LoadingSpinner';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getTarotImage = (name: string) => {
  if (!name) return '';
  
  // Strip numbers and dots from the start (e.g. "0. The Fool" -> "The Fool")
  const cleanName = name.replace(/^\d+\.\s*/, '');

  const majorArcanaMap: { [key: string]: string } = {
    "The Fool": "00_the_fool.png",
    "The Magician": "01_the_magician.jpg",
    "The High Priestess": "02_the_high_priestess.png",
    "The Empress": "03_the_empress.jpg",
    "The Emperor": "04_the_emperor.jpg",
    "The Hierophant": "05_the_hierophant.jpg",
    "The Lovers": "06_the_lovers.jpg",
    "The Chariot": "07_the_chariot.jpg",
    "Strength": "08_strength.jpg",
    "The Hermit": "09_the_hermit.jpg",
    "Wheel of Fortune": "10_wheel_of_fortune.jpg",
    "Justice": "11_justice.jpg",
    "The Hanged Man": "12_the_hanged_man.jpg",
    "Death": "13_death.jpg",
    "Temperance": "14_temperance.jpg",
    "The Devil": "15_the_devil.jpg",
    "The Tower": "16_the_tower.jpg",
    "The Star": "17_the_star.jpg",
    "The Moon": "18_the_moon.jpg",
    "The Sun": "19_the_sun.jpg",
    "Judgement": "20_judgement.jpg",
    "The World": "21_the_world.jpg"
  };

  // Check Major Arcana first (case-insensitive check)
  const majorKey = Object.keys(majorArcanaMap).find(k => k.toLowerCase() === cleanName.toLowerCase().trim());
  if (majorKey) {
    return `${API_URL}/tarot_images/${majorArcanaMap[majorKey]}`;
  }

  const minorParts = cleanName.split(/ of /i);
  if (minorParts.length === 2) {
    const rank = minorParts[0].toLowerCase().trim();
    const suit = minorParts[1].toLowerCase().trim();
    
    const rankMap: { [key: string]: string } = {
      'ace': 'ace', 'two': '02', 'three': '03', 'four': '04', 'five': '05',
      'six': '06', 'seven': '07', 'eight': '08', 'nine': '09', 'ten': '10',
      'page': 'page', 'knight': 'knight', 'queen': 'queen', 'king': 'king'
    };

    const finalRank = rankMap[rank] || rank;
    return `${API_URL}/tarot_images/${suit}_${finalRank}.jpg`;
  }

  return '';
};

const ReferenceDetail = ({ user }: any) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ref, setRef] = useState<any>(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('recent');

  const fetchNotes = React.useCallback(async (sortOrder: string) => {
    try {
      const res = await axios.get(`${API_URL}/references/${id}/notes?sort=${sortOrder}`);
      setNotes(res.data);
    } catch (err) {
      console.error('Failed to fetch notes', err);
    }
  }, [id]);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const [refRes] = await Promise.all([
          axios.get(`${API_URL}/references/${id}`)
        ]);
        setRef(refRes.data);
        await fetchNotes(sortBy);
      } catch (err) {
        console.error('Failed to fetch reference detail', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [id, sortBy, fetchNotes]);

  const addNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
        alert("Please login to add a note.");
        return;
    }
    try {
      await axios.post(`${API_URL}/references/${id}/notes`, 
        { content: newNote },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setNewNote('');
      fetchNotes(sortBy);
    } catch (err) {
      console.error('Failed to add note', err);
    }
  };

  const toggleLike = async (noteId: number) => {
    if (!user) {
      alert("Please login to like notes.");
      return;
    }
    try {
      await axios.post(`${API_URL}/likes`, 
        { note_id: noteId },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      fetchNotes(sortBy);
    } catch (err) {
      console.error('Failed to toggle like', err);
    }
  };

  const addToSpell = () => {
    if (!user) {
      alert("Please login to add this to a spell.");
      return;
    }
    navigate('/spells', { state: { prefillIngredient: ref.name } });
  };

  if (loading) return <LoadingSpinner message="Unveiling the mystery..." />;
  if (!ref) return <div className="content">Reference not found.</div>;

  return (
    <div className="content">
      <button onClick={() => navigate(-1)} style={{ marginBottom: '1rem' }}>&larr; Back to Library</button>
      
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <span className="badge">{ref.category}</span>
          {user && (
            <button 
              onClick={addToSpell}
              style={{ 
                padding: '0.4rem 0.8rem', 
                fontSize: '0.85rem', 
                backgroundColor: 'var(--accent-purple)',
                color: 'white',
                border: '1px solid var(--gold)'
              }}
            >
              ✨ Add to Spell Log
            </button>
          )}
        </div>

        <div style={{ 
          display: 'flex', 
          gap: '2rem', 
          marginTop: '1.5rem',
          flexDirection: window.innerWidth < 768 ? 'column' : 'row'
        }}>
          {ref.category === 'tarot' && (
            <div style={{ 
              flex: '0 0 250px', 
              border: '2px solid var(--gold)', 
              borderRadius: '12px', 
              overflow: 'hidden',
              boxShadow: '0 10px 25px rgba(0,0,0,0.6)',
              height: 'fit-content'
            }}>
              <div className="protected-image-container">
                <div className="image-overlay" onContextMenu={(e) => e.preventDefault()}></div>
                <img 
                  src={getTarotImage(ref.name)} 
                  alt={ref.name} 
                  className="no-save"
                  style={{ width: '100%', display: 'block' }}
                  onError={(e: any) => { e.target.src = 'https://via.placeholder.com/300x520/1a0b2e/d4af37?text=Arcane+Circle'; }}
                />
              </div>
            </div>
          )}
          
          <div style={{ flex: 1 }}>
            <h1 style={{ color: 'var(--gold)', marginTop: 0 }}>{ref.name}</h1>
            <p style={{ fontSize: '1.2rem', lineHeight: '1.6' }}><strong>Description:</strong> {ref.description}</p>
            <p style={{ fontSize: '1.1rem', color: 'var(--pale-gold)' }}><strong>Properties & Correspondences:</strong> {ref.properties}</p>
          </div>
        </div>
      </div>

      {ref.long_description && (
        <div className="card rich-content">
          <h2 style={{ color: 'var(--gold)', borderBottom: '1px solid var(--accent-purple)', paddingBottom: '0.5rem' }}>
            Deep Lore & Mystical Origins
          </h2>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {ref.long_description}
          </ReactMarkdown>
        </div>
      )}

      <div className="notes-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2>Community Knowledge & Personal Notes</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span style={{ fontSize: '0.9rem', opacity: 0.8 }}>Sort by:</span>
            <select 
              value={sortBy} 
              onChange={e => setSortBy(e.target.value)}
              style={{ width: 'auto', marginBottom: 0, padding: '0.3rem 0.6rem' }}
            >
              <option value="recent">Most Recent</option>
              <option value="likes">Most Helpful (Likes)</option>
            </select>
          </div>
        </div>

        {user ? (
          <form onSubmit={addNote} style={{ marginBottom: '2rem' }}>
            <textarea 
              placeholder="Share your experience, ritual results, or additional lore about this item..." 
              value={newNote} 
              onChange={e => setNewNote(e.target.value)}
              rows={4}
              required
            />
            <button type="submit">Add Community Note</button>
          </form>
        ) : (
          <p><em>Please <a href="/auth" style={{ color: 'var(--gold)' }}>login</a> to share your knowledge.</em></p>
        )}

        <div className="notes-list">
          {notes.length === 0 ? (
            <p>No community notes yet. Be the first to contribute!</p>
          ) : (
            notes.map((note: any) => (
              <div key={note.id} className="card" style={{ borderLeft: '4px solid var(--gold)' }}>
                <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>{note.content}</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ opacity: 0.7, fontSize: '0.8rem' }}>
                    <span>By: {note.username}</span>
                    <span style={{ margin: '0 0.5rem' }}>|</span>
                    <span>{new Date(note.created_at).toLocaleDateString()}</span>
                  </div>
                  <button 
                    onClick={() => toggleLike(note.id)} 
                    className="like-button"
                    style={{ 
                        padding: '0.3rem 0.8rem', 
                        fontSize: '0.9rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.4rem'
                    }}
                  >
                    <span>✨</span> {note.like_count}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ReferenceDetail;